import React from "react";
import { useNavigate } from "react-router-dom";
import { ExternalLink, BookMarked, Sparkles, ArrowLeft, Library } from "lucide-react";

/**
 * Card que dirige al usuario a una guía oficial pro-se (Stanford Law + Centro Legal
 * de la Raza + CLSEPA, 2018) para asilo defensivo I-589 en español.
 *
 * El enlace abre en una pestaña NUEVA (target="_blank" + rel="noopener noreferrer")
 * para que el usuario nunca pierda su sesión en miapoyo; al cerrar la pestaña del
 * PDF, vuelve automáticamente a su Dashboard.
 */
export default function ValuableResourceCard() {
  const navigate = useNavigate();
  const RESOURCE_URL =
    "https://law.stanford.edu/wp-content/uploads/2017/05/SF-EOIR-Defensive-Asylum-Pro-Se-Guide_Spanish-FINAL-Feb-2018.pdf";

  return (
    <div
      data-testid="valuable-resource-card"
      className="relative bg-gradient-to-br from-blue-900 via-indigo-900 to-blue-800 text-white rounded-2xl p-5 sm:p-6 overflow-hidden shadow-[0_15px_40px_rgba(30,58,138,0.25)] border-2 border-blue-400/30"
    >
      {/* Decorative glow */}
      <div className="absolute -top-12 -right-12 w-44 h-44 bg-amber-400/20 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-emerald-400/15 rounded-full blur-3xl pointer-events-none" />

      <div className="relative">
        <div className="flex items-start gap-3 mb-3">
          <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-amber-400 text-blue-950 flex items-center justify-center shadow-lg">
            <BookMarked className="w-6 h-6" strokeWidth={2.5} />
          </div>
          <div className="min-w-0">
            <p className="text-[10px] sm:text-xs uppercase tracking-widest font-black text-amber-300 mb-1 flex items-center gap-1.5">
              <Sparkles className="w-3 h-3" />
              Información valiosa
            </p>
            <h3 className="text-lg sm:text-xl font-black leading-tight">
              Guía oficial pro-se de Stanford Law
            </h3>
          </div>
        </div>

        <p className="text-sm text-blue-100 leading-snug mb-4">
          Guía gratuita de auto-representación para asilo defensivo en español, creada por{" "}
          <span className="font-black text-white">Stanford Law School</span>,{" "}
          <span className="font-black text-white">Centro Legal de la Raza</span> y{" "}
          <span className="font-black text-white">CLSEPA</span>. Cubre paso a paso el Formulario
          I-589, audiencias en corte, permiso de trabajo I-765, y casos ejemplo (María y Miguel).
        </p>

        <a
          href={RESOURCE_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="valuable-resource-open"
          className="w-full flex items-center justify-center gap-2 h-13 sm:h-14 px-4 py-3 bg-amber-400 hover:bg-amber-500 text-blue-950 font-black rounded-xl text-sm sm:text-base transition-colors shadow-lg shadow-amber-500/30"
        >
          Haz clic aquí para ver la guía
          <ExternalLink className="w-4 h-4 sm:w-5 sm:h-5" strokeWidth={3} />
        </a>

        <button
          onClick={() => navigate("/manuales-oficiales")}
          data-testid="valuable-resource-see-all"
          className="w-full flex items-center justify-center gap-2 h-11 mt-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white font-bold rounded-xl text-xs sm:text-sm transition-colors border border-white/20"
        >
          <Library className="w-4 h-4" />
          Ver todos los manuales oficiales (14 recursos)
        </button>

        <div className="mt-3 flex items-start gap-2 text-[11px] text-blue-200 leading-snug">
          <ArrowLeft className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
          <span>
            La guía se abre en una <span className="font-bold">pestaña nueva</span>. Tu sesión de
            miapoyo queda intacta — solo cierra la pestaña del PDF y regresa aquí.
          </span>
        </div>

        <p className="mt-3 text-[10px] text-blue-300 leading-snug">
          Documento de dominio público publicado en 2018. Algunos plazos pueden haber cambiado —
          úsala como referencia educativa. miapoyo no está afiliado a las instituciones citadas.
        </p>
      </div>
    </div>
  );
}
